import { useState, useMemo } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Label } from './ui/label';
import { Input } from './ui/input';
import { Slider } from './ui/slider';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { TrendingUp, DollarSign, Clock, Zap, Calculator } from 'lucide-react';

/**
 * ROI Calculator Component
 * 
 * Interactive calculator showing the ROI of Pathkit based on:
 * - Number of consultants
 * - Projects per month
 * - Hourly billing rate
 * 
 * Shows time saved, revenue impact, and cost comparison
 */
export function ROICalculator() {
  const [consultants, setConsultants] = useState(5);
  const [projectsPerMonth, setProjectsPerMonth] = useState(15); // 5 consultants × 3 projects each
  const [hourlyRate, setHourlyRate] = useState(225);
  const [hoursSaved, setHoursSaved] = useState(30);

  // Calculate pricing tier
  const getPricing = useMemo(() => {
    if (consultants === 1) {
      return { tier: 'Solo', pricePerSeat: 49, totalMonthly: 49 };
    } else if (consultants <= 10) {
      const pricePerSeat = consultants >= 3 ? 89 : 99;
      return { tier: 'Small Firm', pricePerSeat, totalMonthly: consultants * pricePerSeat };
    } else {
      // Enterprise with volume discounts
      let pricePerSeat = 79;
      if (consultants >= 101) pricePerSeat = 67;
      else if (consultants >= 51) pricePerSeat = 71;
      else if (consultants >= 26) pricePerSeat = 75;
      
      return { tier: 'Enterprise', pricePerSeat, totalMonthly: consultants * pricePerSeat };
    }
  }, [consultants]);

  // Calculate value delivered
  const calculations = useMemo(() => {
    const totalHoursSaved = projectsPerMonth * hoursSaved;
    const monthlyValue = totalHoursSaved * hourlyRate;
    const annualValue = monthlyValue * 12;
    
    const monthlyCost = getPricing.totalMonthly;
    const annualCost = monthlyCost * 12;
    
    const monthlyNetGain = monthlyValue - monthlyCost;
    const annualNetGain = annualValue - annualCost;
    
    const roi = ((monthlyValue - monthlyCost) / monthlyCost) * 100;
    
    const paybackMinutes = (monthlyCost / hourlyRate) * 60;
    
    return {
      totalHoursSaved,
      monthlyValue,
      annualValue,
      monthlyCost,
      annualCost,
      monthlyNetGain,
      annualNetGain,
      roi,
      paybackMinutes
    };
  }, [consultants, projectsPerMonth, hourlyRate, hoursSaved, getPricing]);

  const formatCurrency = (num: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(num);
  };

  const formatNumber = (num: number) => {
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(num);
  };

  return (
    <div className="w-full max-w-6xl mx-auto space-y-6">
      {/* Header */}
      <div className="text-center space-y-2">
        <div className="flex items-center justify-center gap-2">
          <Calculator className="h-8 w-8 text-blue-600" />
          <h2 className="text-blue-600">ROI Calculator</h2>
        </div>
        <p className="text-gray-600">
          See how much time and money Pathkit will save your consulting practice
        </p>
        <p className="text-sm text-gray-500 mt-1">
          Industry average: Consultants run 3-4 projects concurrently at $225/hr billing rate
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left: Inputs */}
        <Card>
          <CardHeader>
            <CardTitle>Your Practice</CardTitle>
            <CardDescription>Adjust these values to match your consulting firm</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Number of Consultants */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label>Number of Consultants</Label>
                <Input
                  type="number"
                  value={consultants}
                  onChange={(e) => setConsultants(Math.max(1, Math.min(200, parseInt(e.target.value) || 1)))}
                  className="w-20 text-center"
                  min="1"
                  max="200"
                />
              </div>
              <Slider
                value={[consultants]}
                onValueChange={([value]) => setConsultants(value)}
                min={1}
                max={50}
                step={1}
                className="w-full"
              />
              <div className="flex justify-between text-xs text-gray-500">
                <span>1 consultant</span>
                <span>50+ consultants</span>
              </div>
            </div>

            {/* Projects per Month */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label>Projects per Month</Label>
                <Input
                  type="number"
                  value={projectsPerMonth}
                  onChange={(e) => setProjectsPerMonth(Math.max(1, Math.min(200, parseInt(e.target.value) || 1)))}
                  className="w-20 text-center"
                  min="1"
                  max="200"
                />
              </div>
              <Slider
                value={[projectsPerMonth]}
                onValueChange={([value]) => setProjectsPerMonth(value)}
                min={1}
                max={100}
                step={1}
                className="w-full"
              />
              <div className="flex justify-between text-xs text-gray-500">
                <span>1 project</span>
                <span>100+ projects</span>
              </div>
            </div>

            {/* Hourly Billing Rate */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label>Hourly Billing Rate</Label>
                <div className="flex items-center gap-1">
                  <span className="text-sm text-gray-500">$</span>
                  <Input
                    type="number"
                    value={hourlyRate}
                    onChange={(e) => setHourlyRate(Math.max(50, Math.min(500, parseInt(e.target.value) || 225)))}
                    className="w-24 text-center"
                    min="50"
                    max="500"
                  />
                </div>
              </div>
              <Slider
                value={[hourlyRate]}
                onValueChange={([value]) => setHourlyRate(value)}
                min={100}
                max={400}
                step={5}
                className="w-full"
              />
              <div className="flex justify-between text-xs text-gray-500">
                <span>$100/hr</span>
                <span className="text-blue-600 font-medium">Industry avg: $225/hr</span>
                <span>$400/hr</span>
              </div>
            </div>

            {/* Hours Saved per Project */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label>Hours Saved per Project</Label>
                <Input
                  type="number"
                  value={hoursSaved}
                  onChange={(e) => setHoursSaved(Math.max(1, Math.min(100, parseInt(e.target.value) || 30)))}
                  className="w-20 text-center"
                  min="1"
                  max="100"
                />
              </div>
              <Slider
                value={[hoursSaved]}
                onValueChange={([value]) => setHoursSaved(value)}
                min={10}
                max={80}
                step={5}
                className="w-full"
              />
              <div className="flex justify-between text-xs text-gray-500">
                <span>10 hours</span>
                <span className="text-blue-600 font-medium">Avg: 30-40 hours</span>
                <span>80 hours</span>
              </div>
            </div>

            {/* Pricing Tier Badge */}
            <div className="pt-4 border-t">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Your Pricing Tier:</span>
                <Badge className="bg-blue-600 text-white">
                  {getPricing.tier} - ${getPricing.pricePerSeat}/consultant/month
                </Badge>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Right: Results */}
        <div className="space-y-4">
          {/* Monthly Results */}
          <Card className="border-2 border-blue-500 bg-gradient-to-br from-blue-50 to-white">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <TrendingUp className="h-5 w-5 text-blue-600" />
                Monthly Impact
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <p className="text-xs text-gray-600">Time Saved</p>
                  <p className="text-2xl font-bold text-blue-600">
                    {formatNumber(calculations.totalHoursSaved)} hrs
                  </p>
                </div>
                <div className="space-y-1">
                  <p className="text-xs text-gray-600">Value Created</p>
                  <p className="text-2xl font-bold text-green-600">
                    {formatCurrency(calculations.monthlyValue)}
                  </p>
                </div>
              </div>

              <div className="pt-4 border-t space-y-2">
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Pathkit Cost</span>
                  <span className="font-medium">
                    -{formatCurrency(calculations.monthlyCost)}
                  </span>
                </div>
                <div className="flex justify-between items-center pt-2 border-t border-dashed">
                  <span className="font-medium">Net Gain</span>
                  <span className="text-xl font-bold text-green-600">
                    +{formatCurrency(calculations.monthlyNetGain)}
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Annual Results */}
          <Card className="border-2 border-green-500 bg-gradient-to-br from-green-50 to-white">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <DollarSign className="h-5 w-5 text-green-600" />
                Annual Impact
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Total Value Delivered</span>
                  <span className="text-xl font-bold text-green-600">
                    {formatCurrency(calculations.annualValue)}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Pathkit Cost (Annual)</span>
                  <span className="font-medium">
                    -{formatCurrency(calculations.annualCost)}
                  </span>
                </div>
                <div className="flex justify-between items-center pt-3 border-t-2 border-green-200">
                  <span className="font-bold text-gray-900">Net Gain (Year 1)</span>
                  <span className="text-2xl font-bold text-green-600">
                    +{formatCurrency(calculations.annualNetGain)}
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* ROI Card */}
          <Card className="border-2 border-purple-500 bg-gradient-to-br from-purple-50 to-white">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Zap className="h-5 w-5 text-purple-600" />
                Return on Investment
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="text-center space-y-2">
                <div>
                  <p className="text-sm text-gray-600">ROI</p>
                  <p className="text-4xl font-bold text-purple-600">
                    {formatNumber(calculations.roi)}%
                  </p>
                </div>
                <div className="pt-4 border-t">
                  <p className="text-sm text-gray-600 mb-1">Payback Time</p>
                  <div className="flex items-center justify-center gap-2">
                    <Clock className="h-5 w-5 text-purple-600" />
                    <p className="text-2xl font-bold text-purple-600">
                      {Math.round(calculations.paybackMinutes)} minutes
                    </p>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    of billable work to break even
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* CTA */}
          <Card className="bg-gradient-to-r from-blue-600 to-purple-600 text-white">
            <CardContent className="pt-6 text-center space-y-3">
              <p className="text-sm opacity-90">
                Start saving {formatNumber(calculations.totalHoursSaved)} hours per month
              </p>
              <Button size="lg" className="w-full bg-white text-blue-600 hover:bg-gray-100">
                Start Free 14-Day Trial
              </Button>
              <p className="text-xs opacity-75">
                No credit card required • Full access to all features
              </p>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Bottom Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-6">
        <Card>
          <CardContent className="pt-6 text-center">
            <Clock className="h-8 w-8 text-blue-600 mx-auto mb-2" />
            <p className="text-2xl font-bold text-gray-900">
              {formatNumber(calculations.totalHoursSaved * 12)}
            </p>
            <p className="text-sm text-gray-600">Hours Saved Annually</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6 text-center">
            <DollarSign className="h-8 w-8 text-green-600 mx-auto mb-2" />
            <p className="text-2xl font-bold text-gray-900">
              {formatCurrency(calculations.annualValue)}
            </p>
            <p className="text-sm text-gray-600">Annual Value Delivered</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6 text-center">
            <TrendingUp className="h-8 w-8 text-purple-600 mx-auto mb-2" />
            <p className="text-2xl font-bold text-gray-900">
              {formatNumber(calculations.roi)}%
            </p>
            <p className="text-sm text-gray-600">Return on Investment</p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}